import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaseloadReading, IOutcomeMetric, OutcomeMetricId } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK, STATUS } from './agencyChartTheme';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { ClientReadingAvatar } from './ClientReadingAvatar';
import { HistoryWaitNote } from './HistoryWaitNote';
import { InsightsNarration } from './InsightsNarration';

/** How many of the clients behind a figure the card names before it counts the rest. */
const MAX_NAMED = 4;

const useStyles = createUseStyles({
    report: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 14
    },

    grid: {
        display: 'grid',
        // Three cards share a row in the reading column.
        gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))',
        gap: 14
    },

    card: {
        display: 'flex',
        flexDirection: 'column' as const,
        padding: '15px 16px 14px',
        borderRadius: 12,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'var(--ion-background-color)',

        '@media print': {
            breakInside: 'avoid'
        }
    },

    // Two figures in one card are set apart by a rule, so each keeps its own title and caption.
    figure: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 6,

        '& + &': {
            marginTop: 14,
            paddingTop: 14,
            borderTop: `1px solid ${BRAND.tealEdge}`
        }
    },

    title: {
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.02em',
        color: BRAND.deepTeal
    },

    // Every figure is the good news the caseload can show for itself, so every one reads in the
    // steady green; a client's reading colour never stands for a figure.
    value: {
        fontSize: 34,
        fontWeight: 600,
        lineHeight: 1.05,
        color: STATUS.steady,
        fontVariantNumeric: 'tabular-nums'
    },

    caption: {
        fontSize: 12.5,
        lineHeight: 1.4,
        color: CHART_INK.label
    },

    // In the figure's place when there is nobody to count, so the card says why rather than showing a
    // zero that reads as a finding.
    noValue: {
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.35,
        color: CHART_INK.label
    },

    // The people behind the figure. Set apart from the caption because one is what the number counts
    // and the other is who, and a reader is owed the difference.
    named: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 2,
        marginTop: 8,
        paddingTop: 8,
        borderTop: `1px solid ${BRAND.tealEdge}`,
        fontSize: 12.5,
        lineHeight: 1.45
    },

    // A row the shape of the lists', smaller, and a way into the same detail view.
    client: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '3px 4px',
        margin: '0 -4px',
        border: 0,
        borderRadius: 6,
        background: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:hover, &:focus-visible': {
            background: BRAND.tealWash
        },

        // On paper each name carries its detail, a few lines the avatar sits at the top of.
        '@media print': {
            alignItems: 'flex-start'
        }
    },

    clientWho: {
        fontWeight: 500,
        color: CHART_INK.strongLabel
    },

    clientWhy: {
        color: CHART_INK.label
    },

    namedQuiet: {
        color: CHART_INK.label
    },

    more: {
        alignSelf: 'flex-start',
        marginTop: 4,
        padding: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
        fontSize: 11.5,
        fontWeight: 500,
        color: BRAND.deepTeal,
        cursor: 'pointer',

        '&:hover, &:focus-visible': {
            textDecoration: 'underline'
        },

        '@media print': {
            display: 'none'
        }
    }
});

/**
 * Which card each figure sits in: the two about declines and incidents share the first, and the
 * figure about the record stands on its own at the end. Exhaustive, so a new figure has to be placed.
 */
const CARD_OF: { [key in OutcomeMetricId]: number } = {
    'back-from-a-decline': 0,
    'no-incident-recorded': 0,
    'settled-below-usual': 1,
    'family-entries': 2
};

/** The way to the whole list behind the figure, named for what the list holds. */
const SEE_ALL: { [key in OutcomeMetricId]: string } = {
    'back-from-a-decline': 'See all',
    'no-incident-recorded': 'See all incidents',
    'settled-below-usual': 'See all',
    'family-entries': 'See each client'
};

/** The figures that name their clients under the number, each with what it says when there is nobody to name. */
const NAMES_ITS_CLIENTS: { [key in OutcomeMetricId]?: string } = {
    'settled-below-usual': 'Nobody has had less raised than usual lately.'
};

interface IOutcomeClientProps {
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    clientReading: (careMapId: string) => CaseloadReading | undefined;
    /** Opens the client's detail view, the same one the caseload map and the lists open. */
    onOpenClient: (careMapId: string) => void;
}

/** The first few clients behind a figure, each a way into their own detail, and how many more the list holds. */
const NamedClients: FC<IOutcomeClientProps & { metric: IOutcomeMetric; nobody: string; withDetails: boolean }> = ({
    metric, nobody, withDetails, clientAvatar, clientReading, onOpenClient
}) => {
    const classes = useStyles();
    const narration = useInstance(InsightsNarration);
    const named = withDetails ? metric.clients : metric.clients.slice(0, MAX_NAMED);
    const unnamed = metric.clients.length - named.length;

    return (
        <div className={classes.named} data-id="outcome-named">
            {named.length === 0 && <span className={classes.namedQuiet}>{nobody}</span>}
            {named.map(client => {
                const name = narration.shortName(client.clientName);
                return (
                    <button
                        key={client.careMapId}
                        type="button"
                        className={classes.client}
                        data-id="outcome-named-client"
                        onClick={() => onOpenClient(client.careMapId)}
                    >
                        <ClientReadingAvatar size={22} identity={clientAvatar(client.careMapId)} name={name} reading={clientReading(client.careMapId)} />
                        <span className="mp-mask">
                            <span className={classes.clientWho} data-id="outcome-named-client-name">{name}</span>
                            {withDetails && client.detail && <span className={classes.clientWhy}>: {client.detail}</span>}
                        </span>
                    </button>
                );
            })}
            {unnamed > 0 && <span className={classes.namedQuiet}>and {unnamed} more</span>}
        </div>
    );
};

/**
 * One figure: its number, what it counts, and the way to the people behind it, where how the figure
 * is counted is a press away. Trending Better also names its first few clients under the number.
 */
const OutcomeFigure: FC<IOutcomeClientProps & {
    metric: IOutcomeMetric;
    withDetails: boolean;
    onOpen: (metric: IOutcomeMetric) => void;
}> = ({ metric, withDetails, onOpen, ...clientProps }) => {
    const classes = useStyles();
    const nobody = NAMES_ITS_CLIENTS[metric.id];

    return (
        <div className={classes.figure} data-id="outcome-metric" data-metric={metric.id}>
            <span className={classes.title}>{metric.title}</span>

            {metric.value === undefined
                ? <span className={classes.noValue}>{metric.caption}</span>
                : (
                    <>
                        <span className={classes.value} data-id="outcome-value">{metric.value}</span>
                        <span className={classes.caption}>{metric.caption}</span>
                    </>
                )}

            {nobody !== undefined && metric.value !== undefined && (
                <NamedClients metric={metric} nobody={nobody} withDetails={withDetails} {...clientProps} />
            )}

            <button type="button" className={classes.more} data-id="outcome-see-all" onClick={() => onOpen(metric)}>
                {SEE_ALL[metric.id]} &rsaquo;
            </button>
        </div>
    );
};

/** The outcome figures, and under them what the ones still waiting on the caseload's history are waiting for. */
export const OutcomesReport: FC<IOutcomeClientProps & {
    metrics: IOutcomeMetric[];
    /** What the figures still waiting on the caseload's history are waiting for, under the ones shown. */
    pending?: string;
    /** Names every client a card lists, each with their detail, for a report read without the drill-down. */
    withDetails?: boolean;
    onOpen: (metric: IOutcomeMetric) => void;
}> = ({ metrics, pending, withDetails = false, onOpen, ...clientProps }) => {
    const classes = useStyles();
    const cards: IOutcomeMetric[][] = [];
    for (const metric of metrics) {
        (cards[CARD_OF[metric.id]] ??= []).push(metric);
    }
    const shownCards = cards.filter(shown => shown.length > 0);

    return (
        <div className={classes.report}>
            {shownCards.length > 0 && (
                <div className={classes.grid} data-id="outcomes-report">
                    {shownCards.map(shown => (
                        <div key={shown[0].id} className={classes.card} data-id="outcome-card">
                            {shown.map(metric => (
                                <OutcomeFigure key={metric.id} metric={metric} withDetails={withDetails} onOpen={onOpen} {...clientProps} />
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {pending && <HistoryWaitNote panel="outcomes">{pending}</HistoryWaitNote>}
        </div>
    );
};
